export type Locale = 'en' | 'fr';

export const translations: Record<Locale, Record<string, string>> = {
  en: {
    // Nav
    'nav.menu': 'Menu',
    'nav.dashboard': 'Dashboard',
    'nav.commissions': 'Commissions',
    'nav.book': 'Book',
    'nav.bookOfBusiness': 'Book of Business',
    'nav.points': 'Points',
    'nav.hierarchy': 'Hierarchy',
    'nav.support': 'Support',
    'nav.feedback': 'Feedback',
    'nav.teamSummary': 'Team Summary',
    'nav.navigation': 'Navigation',
    'nav.settings': 'Settings',
    'nav.editProfile': 'Edit Profile',
    'nav.sendFeedback': 'Send Feedback',
    'nav.darkMode': 'Dark Mode',
    'nav.exitToMyWFG': 'Exit to MyWFG Homepage',

    // Page titles
    'page.commissionsDetail': 'Commissions Detail',
    'page.bookOfBusiness': 'Book of Business',
    'page.pointsDetail': 'Points Detail',
    'page.teamSummary': 'Team Summary',
    'page.policyDetail': 'Policy Detail',
    'page.agentDetail': 'Agent Detail',
    'page.notFound': 'Page Not Found',
    'page.notFoundMessage': "The page you're looking for doesn't exist.",
    'page.goHome': 'Go to Commissions',

    // Header / WFG
    'header.title': 'WFG Dashboard',

    // Commissions
    'commissions.search': 'Search',
    'commissions.searchPlaceholder': 'Search agents...',
    'commissions.filters': 'Filters',
    'commissions.level': 'Level',
    'commissions.r12Cashflow': 'R12 Cashflow',
    'commissions.r12NPR': 'R12 NPR',
    'commissions.visibleColumns': 'Visible Columns',
    'commissions.reset': 'Reset',
    'commissions.apply': 'Apply',
    'commissions.mtd': 'MTD',
    'commissions.mtdNPR': 'MTD NPR',
    'commissions.ytd': 'YTD',
    'commissions.ytdNPR': 'YTD NPR',
    'commissions.team': 'Team',
    'commissions.r12CF': 'R12 CF',
    'commissions.chart': 'Chart',
    'commissions.name': 'Name',
    'commissions.lvl': 'LVL',
    'commissions.mtdNetNPR': 'MTD Net/NPR',
    'commissions.rolling12': 'Rolling 12',
    'commissions.baseShop': 'Base Shop',
    'commissions.superbase': 'Super Base',
    'commissions.superteam': 'Super Team',

    // Book of Business
    'bob.policies': 'Policies',
    'bob.agents': 'Agents',
    'bob.policyNumber': 'Policy #',
    'bob.client': 'Client',
    'bob.type': 'Type',
    'bob.status': 'Status',
    'bob.premium': 'Premium',
    'bob.carrier': 'Carrier',
    'bob.effectiveDate': 'Effective Date',
    'bob.active': 'Active',
    'bob.pending': 'Pending',
    'bob.lapsed': 'Lapsed',
    'bob.cancelled': 'Cancelled',
    'bob.totalPolicies': 'Total Policies',
    'bob.totalPremium': 'Total Premium',
    'bob.activePolicies': 'Active Policies',
    'bob.avgPremium': 'Avg Premium',
    'bob.searchPolicies': 'Search policies...',
    'bob.searchAgents': 'Search agents...',
    'bob.allTypes': 'All Types',
    'bob.allStatuses': 'All Statuses',
    'bob.allCarriers': 'All Carriers',

    // Points
    'points.totalPoints': 'Total Points',
    'points.mtdPoints': 'MTD Points',
    'points.ytdPoints': 'YTD Points',
    'points.teamPoints': 'Team Points',
    'points.searchPlaceholder': 'Search...',
    'points.agent': 'Agent',
    'points.pts': 'Points',
    'points.rank': 'Rank',

    // Views
    'view.personal': 'Personal',
    'view.baseShop': 'Base Shop',
    'view.superbase': 'Super Base',
    'view.superteam': 'Super Team',
    'view.team': 'Team',
    'view.organization': 'Organization',
    'view.myWFG': 'My WFG',
    'view.myBusiness': 'My Business',
    'view.tools': 'Tools & Resources',

    // Feedback modal
    'feedback.title': 'Send Feedback',
    'feedback.ratingLabel': "How's your experience so far?",
    'feedback.categoryLabel': "What's this about?",
    'feedback.optional': 'optional',
    'feedback.messageLabel': 'Tell us more',
    'feedback.messagePlaceholder': "What's working? What could be better?",
    'feedback.cancel': 'Cancel',
    'feedback.send': 'Send Feedback',
    'feedback.thanks': 'Thank you!',
    'feedback.thanksMessage': 'Your feedback helps us improve.',
    'feedback.poor': 'Poor',
    'feedback.fair': 'Fair',
    'feedback.good': 'Good',
    'feedback.great': 'Great',
    'feedback.excellent': 'Excellent',
    'feedback.catNavigation': 'Navigation & Layout',
    'feedback.catData': 'Data & Reports',
    'feedback.catPerformance': 'Performance & Speed',
    'feedback.catDesign': 'Visual Design',
    'feedback.catMissing': 'Missing Feature',
    'feedback.catOther': 'Other',

    // Welcome modal
    'welcome.title': 'Welcome to the New WFG Dashboard',
    'welcome.subtitle': 'Early access — MVP Preview',
    'welcome.availableNow': "What's available now",
    'welcome.available1': 'Commissions detail with filtering and views',
    'welcome.available2': 'Book of Business with policy details',
    'welcome.available3': 'Points tracking and team breakdown',
    'welcome.notYet': 'Not yet available',
    'welcome.notYet1': 'Main dashboard landing page',
    'welcome.notYet2': 'Reports and export functionality',
    'welcome.notYet3': 'Hierarchy visualization',
    'welcome.notYet4': 'Notifications and alerts',
    'welcome.comingSoon': 'Coming soon',
    'welcome.coming1': 'Personalized dashboard with widgets',
    'welcome.coming2': 'Real-time notifications',
    'welcome.coming3': 'Advanced reporting and exports',
    'welcome.coming4': 'Mobile-optimized experience',
    'welcome.feedbackNote': 'Your feedback shapes what we build next. Use the feedback button anytime.',
    'welcome.getStarted': 'Get Started →',
    'welcome.setupTitle': 'Set up your profile',
    'welcome.setupSubtitle': 'You can change these anytime from the menu.',
    'welcome.emailLabel': 'Email address',
    'welcome.emailHint': '(for 2FA verification)',
    'welcome.emailPlaceholder': 'your.email@example.com',
    'welcome.mobileLabel': 'Mobile number',
    'welcome.mobileHint': '(optional, for SMS alerts)',
    'welcome.mobilePlaceholder': '+1 (555) 000-0000',
    'welcome.regionLabel': 'Region',
    'welcome.regionPlaceholder': 'Select your state or province',
    'welcome.languageLabel': 'Language',
    'welcome.langEnglish': '🇺🇸 English',
    'welcome.langFrench': '🇨🇦 Français',
    'welcome.back': '← Back',
    'welcome.completeSetup': 'Complete Setup',

    // Profile modal
    'profile.title': 'Edit Profile',
    'profile.save': 'Save Changes',
    'profile.cancel': 'Cancel',

    // Pagination
    'pagination.showing': 'Showing',
    'pagination.of': 'of',
    'pagination.results': 'results',
    'pagination.perPage': 'per page',
    'pagination.prev': 'Previous',
    'pagination.next': 'Next',

    // Table
    'table.noResults': 'No results found',
    'table.sortAsc': 'Sort ascending',
    'table.sortDesc': 'Sort descending',

    // Footer
    'footer.copyright': '© 2026 World Financial Group. All rights reserved.',
    'footer.poweredBy': 'Powered by WFG Digital',

    // Common
    'common.loading': 'Loading...',
    'common.close': 'Close',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.back': 'Back',
    'common.viewAll': 'View All',
    'common.details': 'Details',
  },

  fr: {
    // Nav
    'nav.menu': 'Menu',
    'nav.dashboard': 'Tableau de bord',
    'nav.commissions': 'Commissions',
    'nav.book': 'Portefeuille',
    'nav.bookOfBusiness': 'Portefeuille d\'affaires',
    'nav.points': 'Points',
    'nav.hierarchy': 'Hiérarchie',
    'nav.support': 'Aide',
    'nav.feedback': 'Commentaires',
    'nav.teamSummary': 'Résumé de l\'équipe',
    'nav.navigation': 'Navigation',
    'nav.settings': 'Paramètres',
    'nav.editProfile': 'Modifier le profil',
    'nav.sendFeedback': 'Envoyer des commentaires',
    'nav.darkMode': 'Mode sombre',
    'nav.exitToMyWFG': 'Retour à la page d\'accueil MyWFG',

    // Page titles
    'page.commissionsDetail': 'Détail des commissions',
    'page.bookOfBusiness': 'Portefeuille d\'affaires',
    'page.pointsDetail': 'Détail des points',
    'page.teamSummary': 'Résumé de l\'équipe',
    'page.policyDetail': 'Détail de la police',
    'page.agentDetail': 'Détail de l\'agent',
    'page.notFound': 'Page introuvable',
    'page.notFoundMessage': 'La page que vous recherchez n\'existe pas.',
    'page.goHome': 'Aller aux commissions',

    // Header
    'header.title': 'Tableau de bord WFG',

    // Commissions
    'commissions.search': 'Rechercher',
    'commissions.searchPlaceholder': 'Rechercher des agents...',
    'commissions.filters': 'Filtres',
    'commissions.level': 'Niveau',
    'commissions.r12Cashflow': 'Flux de trésorerie R12',
    'commissions.r12NPR': 'RPP R12',
    'commissions.visibleColumns': 'Colonnes visibles',
    'commissions.reset': 'Réinitialiser',
    'commissions.apply': 'Appliquer',
    'commissions.mtd': 'Mois en cours',
    'commissions.mtdNPR': 'RPP mois en cours',
    'commissions.ytd': 'Cumul annuel',
    'commissions.ytdNPR': 'RPP cumul annuel',
    'commissions.team': 'Équipe',
    'commissions.r12CF': 'FT R12',
    'commissions.chart': 'Graphique',
    'commissions.name': 'Nom',
    'commissions.lvl': 'NIV',
    'commissions.mtdNetNPR': 'Net/RPP mois en cours',
    'commissions.rolling12': '12 mois glissants',
    'commissions.baseShop': 'Base',
    'commissions.superbase': 'Super Base',
    'commissions.superteam': 'Super-équipe',

    // Book of Business
    'bob.policies': 'Polices',
    'bob.agents': 'Agents',
    'bob.policyNumber': 'N° de police',
    'bob.client': 'Client',
    'bob.type': 'Type',
    'bob.status': 'Statut',
    'bob.premium': 'Prime',
    'bob.carrier': 'Assureur',
    'bob.effectiveDate': 'Date d\'effet',
    'bob.active': 'Active',
    'bob.pending': 'En attente',
    'bob.lapsed': 'Échue',
    'bob.cancelled': 'Annulée',
    'bob.totalPolicies': 'Total des polices',
    'bob.totalPremium': 'Prime totale',
    'bob.activePolicies': 'Polices actives',
    'bob.avgPremium': 'Prime moyenne',
    'bob.searchPolicies': 'Rechercher des polices...',
    'bob.searchAgents': 'Rechercher des agents...',
    'bob.allTypes': 'Tous les types',
    'bob.allStatuses': 'Tous les statuts',
    'bob.allCarriers': 'Tous les assureurs',

    // Points
    'points.totalPoints': 'Points totaux',
    'points.mtdPoints': 'Points du mois',
    'points.ytdPoints': 'Points cumulés',
    'points.teamPoints': 'Points d\'équipe',
    'points.searchPlaceholder': 'Rechercher...',
    'points.agent': 'Agent',
    'points.pts': 'Points',
    'points.rank': 'Rang',

    // Views
    'view.personal': 'Personnel',
    'view.baseShop': 'Base',
    'view.superbase': 'Super Base',
    'view.superteam': 'Super-équipe',
    'view.team': 'Équipe',
    'view.organization': 'Organisation',
    'view.myWFG': 'Mon WFG',
    'view.myBusiness': 'Mes affaires',
    'view.tools': 'Outils et ressources',

    // Feedback modal
    'feedback.title': 'Envoyer des commentaires',
    'feedback.ratingLabel': 'Comment est votre expérience jusqu\'à présent?',
    'feedback.categoryLabel': 'De quoi s\'agit-il?',
    'feedback.optional': 'facultatif',
    'feedback.messageLabel': 'Dites-nous en plus',
    'feedback.messagePlaceholder': 'Qu\'est-ce qui fonctionne? Qu\'est-ce qui pourrait être amélioré?',
    'feedback.cancel': 'Annuler',
    'feedback.send': 'Envoyer',
    'feedback.thanks': 'Merci!',
    'feedback.thanksMessage': 'Vos commentaires nous aident à nous améliorer.',
    'feedback.poor': 'Mauvais',
    'feedback.fair': 'Passable',
    'feedback.good': 'Bon',
    'feedback.great': 'Très bien',
    'feedback.excellent': 'Excellent',
    'feedback.catNavigation': 'Navigation et mise en page',
    'feedback.catData': 'Données et rapports',
    'feedback.catPerformance': 'Performance et vitesse',
    'feedback.catDesign': 'Design visuel',
    'feedback.catMissing': 'Fonctionnalité manquante',
    'feedback.catOther': 'Autre',

    // Welcome modal
    'welcome.title': 'Bienvenue sur le nouveau tableau de bord WFG',
    'welcome.subtitle': 'Accès anticipé — Aperçu MVP',
    'welcome.availableNow': 'Ce qui est disponible maintenant',
    'welcome.available1': 'Détail des commissions avec filtres et vues',
    'welcome.available2': 'Portefeuille d\'affaires avec détails des polices',
    'welcome.available3': 'Suivi des points et répartition par équipe',
    'welcome.notYet': 'Pas encore disponible',
    'welcome.notYet1': 'Page d\'accueil du tableau de bord',
    'welcome.notYet2': 'Rapports et fonctionnalité d\'exportation',
    'welcome.notYet3': 'Visualisation de la hiérarchie',
    'welcome.notYet4': 'Notifications et alertes',
    'welcome.comingSoon': 'Bientôt disponible',
    'welcome.coming1': 'Tableau de bord personnalisé avec widgets',
    'welcome.coming2': 'Notifications en temps réel',
    'welcome.coming3': 'Rapports avancés et exportations',
    'welcome.coming4': 'Expérience optimisée pour mobile',
    'welcome.feedbackNote': 'Vos commentaires façonnent ce que nous construisons ensuite.',
    'welcome.getStarted': 'Commencer →',
    'welcome.setupTitle': 'Configurez votre profil',
    'welcome.setupSubtitle': 'Vous pouvez modifier ces paramètres à tout moment depuis le menu.',
    'welcome.emailLabel': 'Adresse courriel',
    'welcome.emailHint': '(pour la vérification 2FA)',
    'welcome.emailPlaceholder': 'votre.courriel@exemple.com',
    'welcome.mobileLabel': 'Numéro de mobile',
    'welcome.mobileHint': '(facultatif, pour les alertes SMS)',
    'welcome.mobilePlaceholder': '+1 (555) 000-0000',
    'welcome.regionLabel': 'Région',
    'welcome.regionPlaceholder': 'Sélectionnez votre province ou état',
    'welcome.languageLabel': 'Langue',
    'welcome.langEnglish': '🇺🇸 English',
    'welcome.langFrench': '🇨🇦 Français',
    'welcome.back': '← Retour',
    'welcome.completeSetup': 'Terminer la configuration',

    // Profile modal
    'profile.title': 'Modifier le profil',
    'profile.save': 'Enregistrer',
    'profile.cancel': 'Annuler',

    // Pagination
    'pagination.showing': 'Affichage de',
    'pagination.of': 'sur',
    'pagination.results': 'résultats',
    'pagination.perPage': 'par page',
    'pagination.prev': 'Précédent',
    'pagination.next': 'Suivant',

    // Table
    'table.noResults': 'Aucun résultat trouvé',
    'table.sortAsc': 'Trier par ordre croissant',
    'table.sortDesc': 'Trier par ordre décroissant',

    // Footer
    'footer.copyright': '© 2026 World Financial Group. Tous droits réservés.',
    'footer.poweredBy': 'Propulsé par WFG Digital',

    // Common
    'common.loading': 'Chargement...',
    'common.close': 'Fermer',
    'common.save': 'Enregistrer',
    'common.cancel': 'Annuler',
    'common.back': 'Retour',
    'common.viewAll': 'Voir tout',
    'common.details': 'Détails',
  },
};
